import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type FormEvent, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { sessionQuery } from '../../lib/session';
import { LearnMore } from '../guidance/learn-more';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { FormError } from './auth-layout';

interface ReauthApi {
  /** Runs `fn`; if the server asks for a fresh authenticator code, asks for it once and retries. */
  withReauth<T>(fn: () => Promise<T>): Promise<T>;
}
const Ctx = createContext<ReauthApi | null>(null);
export const useReauth = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useReauth needs <ReauthProvider>');
  return c;
};

/** Step-up authentication for admin actions and exports (PLAN §11). */
export function ReauthProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<{ resolve: () => void; reject: (e: unknown) => void } | null>(null);
  const qc = useQueryClient();

  const ask = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        pending.current = { resolve, reject };
        setCode('');
        setError(null);
        setOpen(true);
      }),
    [],
  );

  const withReauth = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      try {
        return await fn();
      } catch (e) {
        if (!(e instanceof ApiError && e.code === 'reauth_required')) throw e;
        await ask();
        return fn();
      }
    },
    [ask],
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api('/auth/reauth', { body: { code } });
      await qc.invalidateQueries({ queryKey: sessionQuery.queryKey });
      setOpen(false);
      pending.current?.resolve();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    }
  };

  return (
    <Ctx.Provider value={{ withReauth }}>
      {children}
      <Dialog.Root
        open={open}
        onOpenChange={o => {
          if (!o) {
            setOpen(false);
            pending.current?.reject(new ApiError(403, 'reauth_cancelled', 'Cancelled.'));
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 space-y-3 rounded-lg border bg-surface-raised p-5 shadow-lg">
            <Dialog.Title className="font-semibold">Confirm it's you</Dialog.Title>
            <Dialog.Description className="text-sm text-muted-foreground">
              Admin changes, exports and new backup codes need a fresh code from your authenticator app.{' '}
              <LearnMore slug="keeping-your-account-safe" section="confirm-its-you" label="Why?" />
            </Dialog.Description>
            <form onSubmit={submit} className="space-y-3">
              <label htmlFor="reauth-code" className="text-sm font-medium">
                Authenticator code
              </label>
              <Input
                id="reauth-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                className="tabular tracking-widest"
              />
              <FormError message={error} />
              <Button type="submit" className="w-full" disabled={code.length !== 6}>
                Confirm
              </Button>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Ctx.Provider>
  );
}
